import { baseApi } from '@/admin/baseApi';
import { t } from '@/core/localization';
import { providesList } from '@/shared/helpers/api';
import { showApiErrorSnackbar } from '@/shared/helpers/thunk';
export const warehousesApi = baseApi.enhanceEndpoints({ addTagTypes: ['Stock', 'Crossdocking'] }).injectEndpoints({
    endpoints: (builder) => ({
        getStockSettingsList: builder.query({
            query: () => {
                return {
                    method: 'GET',
                    url: 'stocksettings/stockslist',
                };
            },
            transformResponse: (response) => ({
                stock: Object.hasOwn(response, 1) ? response[1] : [],
                crossdocking: Object.hasOwn(response, 2) ? response[2] : [],
                transfer: [],
            }),
            providesTags: ['Stock'],
            // TODO: (later) this approach will be actual when backend sends different lists ind different requests,
            // now it has the single object with two arrays.
            // providesTags: (result) => providesList(result, 'Stock', 'stockId'),
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                try {
                    await queryFulfilled;
                }
                catch (error) {
                    dispatch(showApiErrorSnackbar({ header: t('get.stock.settings.list.request.failed'), error }));
                }
            },
        }),
        // This two queries was converted to one following below, because the results of both are interdependent.
        // getStockSettings: builder.query<IStock, { stockId: number }>({
        // 	query: ({ stockId }) => {
        // 		return {
        // 			method: 'GET',
        // 			url: `stocksettings/getStock/${stockId}`,
        // 		};
        // 	},
        // 	providesTags: (result, error, { stockId }) => [{ type: 'Stock', stockId }],
        // }),
        // getStockCellRanges: builder.query<IStockCellRange[], void>({
        // 	query: () => {
        // 		return {
        // 			method: 'GET',
        // 			url: 'stocksettings/stockCellRanges',
        // 		};
        // 	},
        // 	forceRefetch: () => true, // TODO: to delete? it is not working
        // 	//providesTags: (result, error, { stockId }) => [{ type: 'Stock', stockId }],
        // 	providesTags: ['Stock'],
        // }),
        // The results of both are interdependent: when cell range added to stockSetting cellRanges (on edit), than it will be removed
        // from unassignedRanges of following query.
        // In case of two separate query it can lead to inconsistency during ui components data refresh.
        getStockSettings: builder.query({
            queryFn: async ({ stockId }, api, extraOptions, baseQuery) => {
                const stockSettingQuery = stockId ? baseQuery({ method: 'GET', url: `stocksettings/getStock/${stockId}` }) : undefined;
                const stockCellRangesQuery = baseQuery({ method: 'GET', url: `stocksettings/stockCellRanges` });
                const bothQueries = Promise.all([stockSettingQuery, stockCellRangesQuery]);
                const result = await bothQueries;
                const stockSettingsResult = result[0];
                const cellRangesResult = result[1];
                if (stockSettingsResult?.error) {
                    api.dispatch(showApiErrorSnackbar({ header: t('get.stock.settings.request.failed'), error: stockSettingsResult.error }));
                    return { error: stockSettingsResult.error };
                }
                if (cellRangesResult.error) {
                    api.dispatch(showApiErrorSnackbar({ header: t('get.stock.settings.request.failed'), error: cellRangesResult.error }));
                    return { error: cellRangesResult.error };
                }
                return {
                    data: { stockSetting: stockSettingsResult?.data, unassignedRanges: cellRangesResult?.data },
                };
            },
            providesTags: (result, error, { stockId }) => [{ type: 'Stock', id: stockId ? stockId : 'LIST' }],
        }),
        // addStockSettings: builder.mutation<void, IStockSettingsAdd>({
        // 	query: (body) => ({
        // 		method: 'POST',
        // 		url: 'stocksettings/addStock',
        // 		body,
        // 	}),
        // 	invalidatesTags: (result, error, stockItems) => [{ type: 'Stock' }],
        // }),
        // We use only one method not to have double mutations in ui component
        editStockSettings: builder.mutation({
            query: (body) => ({
                method: 'POST',
                url: `stocksettings/${body.stockId ? 'update' : 'add'}Stock`,
                body,
            }),
            invalidatesTags: (result, error, { stockId }) => error ? [] : stockId ? [{ type: 'Stock', stockId }] : [{ type: 'Stock', stockId: 'LIST' }],
        }),
        deleteStockSettings: builder.mutation({
            query: ({ stockId }) => ({
                method: 'DELETE',
                url: `stocksettings/deleteStock/${stockId}`,
            }),
            invalidatesTags: (result, error, { stockId }) => (error ? [] : [{ type: 'Stock', stockId }]),
        }),
        // This `getCrossdocks` is duplicated (as `getCrossdockLookup`) through the code, but it is necessary since
        // `providesTags` and `invalidatesTags` do not work when endpoint is called by different apis.
        // It would work if use `dispatch(api.utils.invalidateTags(...))`, but it doesn't seem to be good decision to use it
        // in every mutation.
        // It is used in Admin > Settings > Warehouses, and in Warehouse > CrossdockingSelect component.
        // And also in Supplier in SupplierEditDialog
        getCrossdocks: builder.query({
            query: () => 'crossdocking',
            providesTags: (result) => providesList(result, 'Crossdocking'),
        }),
        getCrossdock: builder.query({
            query: (id) => `crossdocking/${id}`,
            providesTags: (result, error, id) => [{ type: 'Crossdocking', id }],
        }),
        addCrossdock: builder.mutation({
            query: (body) => ({
                method: 'POST',
                url: `crossdocking`,
                body,
            }),
            invalidatesTags: (result, error) => (error ? [] : [{ type: 'Crossdocking', id: 'LIST' }]),
        }),
        editCrossdock: builder.mutation({
            query: (body) => ({
                method: 'POST',
                url: `crossdocking/${body.id}`,
                body,
            }),
            invalidatesTags: (result, error, { id }) => (error ? [] : [{ type: 'Crossdocking', id }]),
        }),
        deleteCrossdock: builder.mutation({
            query: (id) => ({
                method: 'DELETE',
                url: `crossdocking/${id}`,
            }),
            invalidatesTags: (result, error, id) => (error ? [] : [{ type: 'Crossdocking', id }]),
        }),
    }),
});
export const { useGetStockSettingsListQuery, useGetStockSettingsQuery, useEditStockSettingsMutation, useDeleteStockSettingsMutation, useGetCrossdocksQuery, useGetCrossdockQuery, useAddCrossdockMutation, useEditCrossdockMutation, useDeleteCrossdockMutation, } = warehousesApi;
